import React, { useMemo } from 'react';

import { ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { css, Player, type PlayerProps } from '@clubspark-react/ui/core';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { eventsAtom } from 'atoms/events';
import {
  Adornments,
  DrawAdditionalPlayerInformation,
  type DrawSettings,
} from 'components/dialogs/draw/publicDrawDisplaySettings/types';
import { mapParticipants } from 'components/drawStructures/elimination/utils';
import { useCompetitionParticipant } from 'hooks/tournament/useTournamentParticipants';
import useDrawSettings from 'hooks/useDrawSettings';
import { useLevelConfig } from 'hooks/useLevelConfiguration';
import { isParticipantRequestedWithdrawal, isParticipantSuspended } from 'utils/participant';

interface ParticipantProps {
  participantId: string;
  participantName?: string;
  isDoubles?: boolean;
  drawSettings?: DrawSettings;
  showAdditionalInfo?: boolean;
  usePublicDrawSettings?: boolean;
  showSuspensionWithdrawalWarning?: boolean;
  onClick?: PlayerProps['onClick'];
}

const linkStyles = css({
  color: '$primary100main',
});

const defaultDrawSettings: DrawSettings = {
  playerAdornment: Adornments.WTN,
  additionalPlayerInformation: [DrawAdditionalPlayerInformation.CITY_STATE],
};

export const Participant: React.FC<React.PropsWithChildren<ParticipantProps>> = React.memo(
  ({
    participantId,
    participantName,
    isDoubles: isDoublesEventOrMatchUp,
    onClick,
    drawSettings,
    usePublicDrawSettings = false,
    showAdditionalInfo = false,
    showSuspensionWithdrawalWarning = false,
  }) => {
    const { t } = useTranslation();
    const { hideScorecardPlayerActions } = useLevelConfig();
    const events = useAtomValue(eventsAtom);
    const participant = useCompetitionParticipant(participantId);
    const publicDrawSettings = useDrawSettings();
    const resolvedDrawSettings = {
      draw: usePublicDrawSettings ? publicDrawSettings?.draw : drawSettings ?? defaultDrawSettings,
    };
    const players = mapParticipants(
      {
        participant,
        isDoubles: participant?.participantType === ParticipantTypeEnum.PAIR,
      },
      { drawSettings: resolvedDrawSettings },
      isDoublesEventOrMatchUp,
    );

    const isSuspended = useMemo(() => isParticipantSuspended(participant)?.isSuspended, [participant]);

    const { isRequestedWithdrawal, requestedWithdrawnEventIds } = useMemo(
      () => isParticipantRequestedWithdrawal(participant, events),
      [participant, events],
    );

    const warningText = useMemo(() => {
      if (isSuspended && !isRequestedWithdrawal) return t('playerSuspended');
      if (isRequestedWithdrawal) {
        const eventNames = participant?.events
          ?.filter((event) => requestedWithdrawnEventIds?.includes(event?.eventId))
          ?.map((event) => event?.eventName)
          ?.join(', ');
        if (!isSuspended) return t('withdrawalRequest', { eventName: eventNames });
        return t('suspendedAndWithdrawal', { eventName: eventNames });
      }
    }, [t, isSuspended, isRequestedWithdrawal, participant?.events, requestedWithdrawnEventIds]);

    return (
      <>
        {players.map((player) => (
          <Player
            key={player.id}
            adornment="none"
            className={!!onClick && linkStyles()}
            displayWarning={showSuspensionWithdrawalWarning && (isSuspended || isRequestedWithdrawal)}
            player={participantName ? { ...player, name: participantName } : player}
            showAdditionalInfo={showAdditionalInfo}
            warningText={warningText}
            onClick={!hideScorecardPlayerActions && onClick}
          />
        ))}
      </>
    );
  },
);

export default Participant;
